import { REMOVE_ASSOCIATIONS_HINT } from '@/shared/services/api/associationConflict';
import type { AssociatedRecord } from '@/shared/services/api/associationConflict';
import './deleteBlockedNotice.css';

interface DeleteBlockedNoticeProps {
  /** What is still attached, as the platform reported it. Empty draws no rows: only the warning. */
  records: readonly AssociatedRecord[];
  /**
   * The PrimeIcons name a row shows beside its kind of record -- the masters pass the header icon of the master
   * those records belong to (`associationIcon`). Absent, every row shows the link.
   */
  iconFor?: (label: string) => string;
  /** The warning's sentence, where a screen words it its own way (design16's Supplier). Absent, the platform's. */
  hint?: string;
}

/**
 * The body of a refused delete, drawn as `design-refs/Deletepopup.html` draws it: one row per kind of linked
 * record with its count, and the red warning saying what to do about it -- the full width of the dialog, below
 * its icon and title.
 *
 * The dialog around it (MasterConfirmDialog, Organization Setup's ConfirmDeleteDialog) supplies the
 * "Can't delete" title, the explanation under it (`blockedLead`) and the single "Got it". Nothing here is
 * invented: the rows are the platform's own labels and counts, and the warning is the one
 * {@link DeleteAssociationNotice} already shows.
 */
export function DeleteBlockedNotice({ records, iconFor, hint }: DeleteBlockedNoticeProps) {
  return (
    <div className="delete-blocked" role="alert">
      {records.length > 0 ? (
        <ul className="delete-blocked__rows">
          {records.map((record) => (
            <li key={record.label} className="delete-blocked__row" data-kind={record.label}>
              <span className="delete-blocked__row-icon" aria-hidden="true">
                <i className={`pi ${iconFor ? iconFor(record.label) : 'pi-link'}`} />
              </span>
              <span className="delete-blocked__row-label">{record.label}</span>
              <span className="delete-blocked__row-count">{record.count}</span>
            </li>
          ))}
        </ul>
      ) : null}

      <p className="delete-blocked__warning">
        <i className="pi pi-exclamation-circle" aria-hidden="true" />
        <span>{hint ?? REMOVE_ASSOCIATIONS_HINT}</span>
      </p>
    </div>
  );
}
